import React, { useState, useEffect } from "react";
import { Menu, Home, Box, Info, Mail } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import logo from "../assets/images/logo.png";

// Simple fade animation for mobile menu
const animations = `
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

function Header() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProductsDropdownOpen, setIsProductsDropdownOpen] = useState(false);
  // const [isSoftwareDropdownOpen, setIsSoftwareDropdownOpen] = useState(false);
  const [isMobileProductsOpen, setIsMobileProductsOpen] = useState(false);
  const [isFeaturesDropdownOpen, setIsFeaturesDropdownOpen] = useState(false);
  const [isMobileFeaturesOpen, setIsMobileFeaturesOpen] = useState(false);
  const isProductPage = location.pathname === "/products";
  const isHardwarePage = location.pathname === "/hardware";
  const isSoftwarePage = location.pathname === "/software"; // 👇 Automatically close the menu on route change
  const isKDSPage = location.pathname === "/kds";
  const isCDSPage = location.pathname === "/cds";
  useEffect(() => {
    setIsMenuOpen(false);
    setIsProductsDropdownOpen(false);
    setIsFeaturesDropdownOpen(false);
    setIsMobileProductsOpen(false);
    setIsMobileFeaturesOpen(false);
  }, [location.pathname]); // 👇 Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Close dropdown if clicking outside of it
      if (isProductsDropdownOpen && !event.target.closest(".dropdown-container") && !event.target.closest("[data-dropdown-trigger]")) {
        setIsProductsDropdownOpen(false);
      }
      if (isFeaturesDropdownOpen && !event.target.closest(".dropdown-container") && !event.target.closest("[data-dropdown-trigger]")) {
        setIsFeaturesDropdownOpen(false);
      }
    };

    if (isProductsDropdownOpen || isFeaturesDropdownOpen) {
      document.addEventListener("click", handleClickOutside);
      document.addEventListener("touchstart", handleClickOutside);
    }

    return () => {
      document.removeEventListener("click", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [isProductsDropdownOpen, isFeaturesDropdownOpen]);

  // Ensure desktop dropdowns are mutually exclusive
  useEffect(() => {
    if (isProductsDropdownOpen) {
      setIsFeaturesDropdownOpen(false);
    }
  }, [isProductsDropdownOpen]);

  useEffect(() => {
    if (isFeaturesDropdownOpen) {
      setIsProductsDropdownOpen(false);
    }
  }, [isFeaturesDropdownOpen]);

  // Ensure mobile dropdowns are mutually exclusive
  useEffect(() => {
    if (isMobileProductsOpen) {
      setIsMobileFeaturesOpen(false);
    }
  }, [isMobileProductsOpen]);

  useEffect(() => {
    if (isMobileFeaturesOpen) {
      setIsMobileProductsOpen(false);
    }
  }, [isMobileFeaturesOpen]);

  return (
    <>
      {/* Add style tag for animations */}
      <style dangerouslySetInnerHTML={{ __html: animations }} />
      <div className={`${isProductPage || isHardwarePage || isSoftwarePage || isKDSPage || isCDSPage ? "bg-vern py-1" : ""}`}>
        <header className="bg-black text-white py-4 px-6 rounded-full mx-6 my-2 flex justify-between items-center">
          <div
            onClick={(e) => {
              e.preventDefault();
              navigate("/");
            }}
            className="logo flex items-center"
          >
            <img src={logo} alt="VernPOS Logo" className="h-12" />
          </div>{" "}
          {/* Desktop Navigation */}
          <nav className="hidden md:flex space-x-5 lg:space-x-8 font-semibold relative">
            <a
              onClick={(e) => {
                e.preventDefault();
                navigate("/");
              }}
              href="/"
              className="text-white hover:text-vern"
            >
              HOME
            </a>
            {/* Products Dropdown */}
            <div className="relative group">
              <button
                className="text-white hover:text-vern flex items-center focus:outline-none"
                data-dropdown-trigger
                onClick={(e) => {
                  e.stopPropagation();
                  setIsProductsDropdownOpen(!isProductsDropdownOpen);
                }}
                onMouseEnter={() => setIsProductsDropdownOpen(true)}
                onMouseLeave={() => setIsProductsDropdownOpen(false)}
              >
                PRODUCTS
                <svg
                  className={`ml-1 w-4 h-4 transition-transform duration-200 ${isProductsDropdownOpen ? "rotate-180" : ""}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                </svg>
              </button>
              {/* Dropdown Menu */}
              <div
                className={`dropdown-container absolute top-full left-0 mt-2 w-72 bg-black rounded-xl shadow-xl border border-gray-800 transition-all duration-300 z-50 ${
                  isProductsDropdownOpen ? "opacity-100 visible transform translate-y-0" : "opacity-0 invisible transform -translate-y-2"
                }`}
                onMouseEnter={() => setIsProductsDropdownOpen(true)}
                onMouseLeave={() => setIsProductsDropdownOpen(false)}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="p-4">
                  <div className="space-y-2">
                    {/* <div
                      // Software menu area -- trigger Software submenu
                      onMouseEnter={() => setIsSoftwareDropdownOpen(true)}
                      onMouseLeave={() => setIsSoftwareDropdownOpen(false)}
                      onClick={(e) => {
                        e.preventDefault();
                        // Optionally open submenu also on click for accessibility
                        setIsSoftwareDropdownOpen((prev) => !prev);
                      }}
                      className="relative cursor-pointer hover:bg-gray-900 p-3 rounded-lg transition-colors duration-200 flex flex-col md:flex-row md:items-center md:space-x-3 space-y-2 md:space-y-0"
                    > */}
                    <div
                      onClick={(e) => {
                        e.preventDefault();
                        navigate("/software");
                        setIsProductsDropdownOpen(false);
                      }}
                      className="cursor-pointer hover:text-vern hover:bg-gray-900 p-3 rounded-lg transition-colors duration-200 flex items-center space-x-3"
                    >
                      {/* <div className="w-6 h-6 flex items-center justify-center">
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z"
                          ></path>
                        </svg>
                      </div> */}
                      <div className="w-6 h-6 flex items-center justify-center">
                        <svg className="w-5 h-5 text-white" fill="#FFF" stroke="currentColor" viewBox="0 0 512 512">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M435.2 85.333h-85.333V68.266c0-9.386-7.68-17.066-17.067-17.066H179.2c-9.386 0-17.066 7.68-17.066 17.066v17.067H76.8c-14.08 0-25.6 11.52-25.6 25.601v238.933c0 14.08 11.52 25.6 25.6 25.6h128v34.987c-19.456 3.925-34.133 21.162-34.133 41.813 0 4.693 3.84 8.533 8.533 8.533h153.6c4.694 0 8.534-3.84 8.534-8.533 0-20.564-14.678-37.803-34.134-41.813v-34.987h128c14.08 0 25.6-11.52 25.6-25.6V110.933c0-14.08-11.52-25.6-25.6-25.6zm-256-17.067h153.6v187.733H179.2zM76.8 102.399h85.333v153.6c0 9.387 7.68 17.067 17.066 17.067h153.6c9.388 0 17.067-7.681 17.067-17.067v-153.6H435.2c4.693 0 8.534 3.84 8.534 8.534v204.8H68.267v-204.8c0-4.694 3.84-8.534 8.533-8.534zm246.016 341.334H189.184c3.499-9.899 12.971-17.067 24.15-17.067h85.333c11.093 0 20.651 7.168 24.149 17.067zm-32.682-34.134h-68.267v-34.133h68.267zm145.066-51.2H76.8c-4.693 0-8.533-3.841-8.533-8.533v-17.067h375.467v17.067c0 4.693-3.84 8.533-8.534 8.533z"
                          />
                          <path d="M204.8 136.534h102.4c4.692 0 8.532-3.84 8.532-8.533V93.867c0-4.693-3.84-8.533-8.532-8.533H204.8c-4.693 0-8.533 3.84-8.533 8.533v34.134c0 4.693 3.84 8.533 8.533 8.533m8.533-34.133h85.334v17.066h-85.334zm8.533 51.199h-8.533c-4.693 0-8.533 3.84-8.533 8.533s3.84 8.533 8.533 8.533h8.533c4.694 0 8.534-3.84 8.534-8.533s-3.84-8.533-8.534-8.533m38.402 0h-8.534c-4.694 0-8.534 3.84-8.534 8.533s3.84 8.533 8.534 8.533h8.534c4.692 0 8.532-3.84 8.532-8.533s-3.84-8.533-8.532-8.533m38.398 0h-8.533c-4.693 0-8.533 3.84-8.533 8.533s3.84 8.533 8.533 8.533h8.533c4.693 0 8.534-3.84 8.534-8.533s-3.841-8.533-8.534-8.533m-76.8 34.132h-8.533c-4.693 0-8.533 3.84-8.533 8.534 0 4.693 3.84 8.533 8.533 8.533h8.533c4.694 0 8.534-3.84 8.534-8.533 0-4.694-3.84-8.534-8.534-8.534m38.402 0h-8.534c-4.694 0-8.534 3.84-8.534 8.534 0 4.693 3.84 8.533 8.534 8.533h8.534c4.692 0 8.532-3.84 8.532-8.533 0-4.694-3.84-8.534-8.532-8.534m38.398 0h-8.533c-4.693 0-8.533 3.84-8.533 8.534 0 4.693 3.84 8.533 8.533 8.533h8.533c4.693 0 8.534-3.84 8.534-8.533 0-4.694-3.841-8.534-8.534-8.534m-76.8 34.135h-8.533c-4.693 0-8.533 3.84-8.533 8.534 0 4.693 3.84 8.533 8.533 8.533h8.533c4.694 0 8.534-3.84 8.534-8.533 0-4.694-3.84-8.534-8.534-8.534m38.402 0h-8.534c-4.694 0-8.534 3.84-8.534 8.534 0 4.693 3.84 8.533 8.534 8.533h8.534c4.692 0 8.532-3.84 8.532-8.533 0-4.694-3.84-8.534-8.532-8.534m38.398 0h-8.533c-4.693 0-8.533 3.84-8.533 8.534 0 4.693 3.84 8.533 8.533 8.533h8.533c4.693 0 8.534-3.84 8.534-8.533 0-4.694-3.841-8.534-8.534-8.534"></path>
                        </svg>
                      </div>
                      <div className="text-white hover:text-vern flex items-center focus:outline-none">
                        <div>
                          <h3 className="font-semibold text-sm mb-0">POS Software</h3>
                          <p className="text-gray-400 text-xs">POS System Solutions</p>
                        </div>
                        {/* <div>
                          <svg
                            className={`ml-1 w-4 h-4 transition-transform duration-200 ${isSoftwareDropdownOpen ? "rotate-180" : ""}`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                          </svg>
                        </div> */}
                      </div>
                      {/* Software Submenu Dropdown */}
                      {/* <div
                        className={`dropdown-container absolute top-full left-0 md:top-0 md:left-full md:ml-2 w-72 bg-black rounded-xl shadow-xl border border-gray-800 transition-all duration-300 z-50 ${
                          isSoftwareDropdownOpen
                            ? "opacity-100 visible transform translate-y-0 md:translate-x-0"
                            : "opacity-0 invisible transform -translate-y-2 md:-translate-x-2"
                        }`}
                        onMouseEnter={() => setIsSoftwareDropdownOpen(true)}
                        onMouseLeave={() => setIsSoftwareDropdownOpen(false)}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="p-4">
                          <div className="space-y-2">
                            <div
                              onClick={(e) => {
                                e.preventDefault();
                                navigate("/software");
                                setIsProductsDropdownOpen(false);
                              }}
                              className="cursor-pointer hover:text-vern hover:bg-gray-900 p-3 rounded-lg transition-colors duration-200 flex items-center space-x-3"
                            >
                              <div className="w-6 h-6 flex items-center justify-center">
                                <svg className="w-5 h-5 text-white" fill="#FFF" stroke="currentColor" viewBox="0 0 512 512">
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M435.2 85.333h-85.333V68.266c0-9.386-7.68-17.066-17.067-17.066H179.2c-9.386 0-17.066 7.68-17.066 17.066v17.067H76.8c-14.08 0-25.6 11.52-25.6 25.601v238.933c0 14.08 11.52 25.6 25.6 25.6h128v34.987c-19.456 3.925-34.133 21.162-34.133 41.813 0 4.693 3.84 8.533 8.533 8.533h153.6c4.694 0 8.534-3.84 8.534-8.533 0-20.564-14.678-37.803-34.134-41.813v-34.987h128c14.08 0 25.6-11.52 25.6-25.6V110.933c0-14.08-11.52-25.6-25.6-25.6zm-256-17.067h153.6v187.733H179.2zM76.8 102.399h85.333v153.6c0 9.387 7.68 17.067 17.066 17.067h153.6c9.388 0 17.067-7.681 17.067-17.067v-153.6H435.2c4.693 0 8.534 3.84 8.534 8.534v204.8H68.267v-204.8c0-4.694 3.84-8.534 8.533-8.534zm246.016 341.334H189.184c3.499-9.899 12.971-17.067 24.15-17.067h85.333c11.093 0 20.651 7.168 24.149 17.067zm-32.682-34.134h-68.267v-34.133h68.267zm145.066-51.2H76.8c-4.693 0-8.533-3.841-8.533-8.533v-17.067h375.467v17.067c0 4.693-3.84 8.533-8.534 8.533z"
                                  />
                                  <path d="M204.8 136.534h102.4c4.692 0 8.532-3.84 8.532-8.533V93.867c0-4.693-3.84-8.533-8.532-8.533H204.8c-4.693 0-8.533 3.84-8.533 8.533v34.134c0 4.693 3.84 8.533 8.533 8.533m8.533-34.133h85.334v17.066h-85.334zm8.533 51.199h-8.533c-4.693 0-8.533 3.84-8.533 8.533s3.84 8.533 8.533 8.533h8.533c4.694 0 8.534-3.84 8.534-8.533s-3.84-8.533-8.534-8.533m38.402 0h-8.534c-4.694 0-8.534 3.84-8.534 8.533s3.84 8.533 8.534 8.533h8.534c4.692 0 8.532-3.84 8.532-8.533s-3.84-8.533-8.532-8.533m38.398 0h-8.533c-4.693 0-8.533 3.84-8.533 8.533s3.84 8.533 8.533 8.533h8.533c4.693 0 8.534-3.84 8.534-8.533s-3.841-8.533-8.534-8.533m-76.8 34.132h-8.533c-4.693 0-8.533 3.84-8.533 8.534 0 4.693 3.84 8.533 8.533 8.533h8.533c4.694 0 8.534-3.84 8.534-8.533 0-4.694-3.84-8.534-8.534-8.534m38.402 0h-8.534c-4.694 0-8.534 3.84-8.534 8.534 0 4.693 3.84 8.533 8.534 8.533h8.534c4.692 0 8.532-3.84 8.532-8.533 0-4.694-3.84-8.534-8.532-8.534m38.398 0h-8.533c-4.693 0-8.533 3.84-8.533 8.534 0 4.693 3.84 8.533 8.533 8.533h8.533c4.693 0 8.534-3.84 8.534-8.533 0-4.694-3.841-8.534-8.534-8.534m-76.8 34.135h-8.533c-4.693 0-8.533 3.84-8.533 8.534 0 4.693 3.84 8.533 8.533 8.533h8.533c4.694 0 8.534-3.84 8.534-8.533 0-4.694-3.84-8.534-8.534-8.534m38.402 0h-8.534c-4.694 0-8.534 3.84-8.534 8.534 0 4.693 3.84 8.533 8.534 8.533h8.534c4.692 0 8.532-3.84 8.532-8.533 0-4.694-3.84-8.534-8.532-8.534m38.398 0h-8.533c-4.693 0-8.533 3.84-8.533 8.534 0 4.693 3.84 8.533 8.533 8.533h8.533c4.693 0 8.534-3.84 8.534-8.533 0-4.694-3.841-8.534-8.534-8.534"></path>
                                </svg>
                              </div>
                              <div>
                                <h3 className="font-semibold text-sm mb-0">POS</h3>
                                <p className="text-gray-400 text-xs">POS System Solutions</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div> */}
                    </div>
                    <div
                      onClick={(e) => {
                        e.preventDefault();
                        navigate("/hardware");
                        setIsProductsDropdownOpen(false);
                      }}
                      className="cursor-pointer hover:text-vern hover:bg-gray-900 p-3 rounded-lg transition-colors duration-200 flex items-center space-x-3"
                    >
                      <div className="w-6 h-6 flex items-center justify-center">
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                          ></path>
                        </svg>
                      </div>
                      <div>
                        <h3 className="font-semibold text-sm mb-0">Hardware</h3>
                        <p className="text-gray-400 text-xs">AIO Solution For Your Business</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Features Dropdown Menu */}
            <div className="relative group">
              <button
                className="text-white hover:text-vern flex items-center focus:outline-none"
                data-dropdown-trigger
                onClick={(e) => {
                  e.stopPropagation();
                  setIsFeaturesDropdownOpen(!isFeaturesDropdownOpen);
                }}
                onMouseEnter={() => setIsFeaturesDropdownOpen(true)}
                onMouseLeave={() => setIsFeaturesDropdownOpen(false)}
              >
                FEATURES
                <svg
                  className={`ml-1 w-4 h-4 transition-transform duration-200 ${isFeaturesDropdownOpen ? "rotate-180" : ""}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                </svg>
              </button>
              {/* Dropdown Menu */}
              <div
                className={`dropdown-container absolute top-full left-0 mt-2 w-72 bg-black rounded-xl shadow-xl border border-gray-800 transition-all duration-300 z-50 ${
                  isFeaturesDropdownOpen ? "opacity-100 visible transform translate-y-0" : "opacity-0 invisible transform -translate-y-2"
                }`}
                onMouseEnter={() => setIsFeaturesDropdownOpen(true)}
                onMouseLeave={() => setIsFeaturesDropdownOpen(false)}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="p-4">
                  <div className="space-y-2">
                    <div
                      onClick={(e) => {
                        e.preventDefault();
                        navigate("/kds");
                        setIsProductsDropdownOpen(false);
                      }}
                      className="cursor-pointer hover:text-vern hover:bg-gray-900 p-3 rounded-lg transition-colors duration-200 flex items-center space-x-3"
                    >
                      <div className="w-6 h-6 flex items-center justify-center">
                        <svg className="w-5 h-5 text-white" fill="#FFF" stroke="currentColor" viewBox="0 0 512 512">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M456 58H56a24.026 24.026 0 0 0-24 24v272a24.026 24.026 0 0 0 24 24h146.14l-7.577 36H192a32.03 32.03 0 0 0-22.617 9.383A32.03 32.03 0 0 0 160 446a8 8 0 0 0 2.343 5.657A8 8 0 0 0 168 454h176a8 8 0 0 0 5.657-2.343A8 8 0 0 0 352 446a32.03 32.03 0 0 0-9.383-22.617A32.03 32.03 0 0 0 320 414h-2.563l-7.577-36H456a24.03 24.03 0 0 0 24-24V82a24.03 24.03 0 0 0-24-24M333.852 438H178.148A16 16 0 0 1 192 430h128a16 16 0 0 1 13.852 8m-32.766-24h-90.172l7.578-36h75.016zM464 354a8 8 0 0 1-2.345 5.655A8 8 0 0 1 456 362H56a8.01 8.01 0 0 1-8-8v-24h416zm0-40H48V82a8.01 8.01 0 0 1 8-8h400a8.01 8.01 0 0 1 8 8z M383.499 231.852h-6.714A121.49 121.49 0 0 0 264 131.364v-13.641a7.997 7.997 0 0 0-8-8 8 8 0 0 0-8 8v13.641a121.49 121.49 0 0 0-112.782 100.488h-6.719a8.36 8.36 0 0 0-5.695 2.726 8.82 8.82 0 0 0-2.297 6.071 32.93 32.93 0 0 0 31.297 33.629h207.961a31.2 31.2 0 0 0 27.07-16.004 36.93 36.93 0 0 0 4.664-18.422 7.98 7.98 0 0 0-2.342-5.658 8 8 0 0 0-5.658-2.342M256 146.988a105.92 105.92 0 0 1 104.694 84.864H151.312A105.91 105.91 0 0 1 256 146.988M372.906 250.41a15 15 0 0 1-13.141 7.867H151.804a15.3 15.3 0 0 1-13.742-10.425h4.109l227.664.039c.065 0 .122-.038.186-.039h4.088q-.51 1.322-1.203 2.558"
                          ></path>
                        </svg>
                      </div>
                      <div>
                        <h3 className="font-semibold text-sm mb-0">KDS</h3>
                        <p className="text-gray-400 text-xs">Kitchen Display System</p>
                      </div>
                    </div>
                    <div
                      onClick={(e) => {
                        e.preventDefault();
                        navigate("/cds");
                        setIsProductsDropdownOpen(false);
                      }}
                      className="cursor-pointer hover:text-vern hover:bg-gray-900 p-3 rounded-lg transition-colors duration-200 flex items-center space-x-3"
                    >
                      <div className="w-6 h-6 flex items-center justify-center">
                        <svg className="w-5 h-5 text-white" fill="#FFF" stroke="currentColor" viewBox="0 0 512 512">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M337.446 142.553C330.65 135.757 321.614 132.009 312 132H167.996c-9.61.009-18.646 3.757-25.443 10.553C135.757 149.35 132.009 158.386 132 168v136.004c.009 9.61 3.757 18.646 10.553 25.442C149.35 336.243 158.386 339.991 168 340h144.004c9.61-.009 18.646-3.757 25.442-10.554C344.243 322.65 347.991 313.614 348 304V167.996c-.009-9.61-3.757-18.646-10.554-25.443m-141.238 53.239c5.286 5.285 12.313 8.201 19.792 8.208h48.004c7.475-.008 14.502-2.923 19.787-8.208 5.286-5.286 8.201-12.313 8.209-19.792v-20h19.994a11.95 11.95 0 0 1 8.485 3.52A11.95 11.95 0 0 1 324 168v135.994a11.95 11.95 0 0 1-3.52 8.484 11.94 11.94 0 0 1-8.48 3.521H168.005a11.95 11.95 0 0 1-8.484-3.521 11.94 11.94 0 0 1-3.521-8.479V168.005a11.94 11.94 0 0 1 3.52-8.484 11.95 11.95 0 0 1 8.485-3.521H188v20.004c.008 7.475 2.923 14.502 8.208 19.788M212 176v-20h56v19.998c-.001 1.067-.417 2.072-1.174 2.828S265.065 180 264 180h-47.998c-1.067 0-2.072-.417-2.828-1.174S212 177.065 212 176 M167.568 255.574c-2.229 2.261-3.456 5.253-3.456 8.426s1.228 6.165 3.456 8.426 5.204 3.529 8.432 3.574h48.056a11.93 11.93 0 0 0 8.376-3.574c2.228-2.26 3.455-5.252 3.455-8.426s-1.227-6.166-3.455-8.426-5.203-3.529-8.432-3.574h-48.057a11.93 11.93 0 0 0-8.375 3.574M208 284h-32.056c-3.173.045-6.148 1.313-8.376 3.574s-3.456 5.253-3.456 8.426 1.228 6.165 3.456 8.426c2.227 2.259 5.201 3.528 8.432 3.574h32.058a11.94 11.94 0 0 0 8.375-3.574c2.228-2.26 3.455-5.252 3.455-8.426s-1.227-6.166-3.455-8.426c-2.229-2.261-5.204-3.529-8.433-3.574 M496.32 69.399C484.616 34.006 453.093 11.083 415.993 11a84.9 84.9 0 0 0-46.513 13.912A84.9 84.9 0 0 0 339.526 59H47.995c-9.877.01-19.164 3.862-26.149 10.847S11.01 86.118 11 96v272.005c.01 9.878 3.862 19.164 10.847 26.149S38.119 404.991 48 405h147v6h-19c-12.021 0-23.321 4.681-31.819 13.18-8.5 8.499-13.181 19.8-13.181 31.82 0 12.021 4.681 23.321 13.18 31.819C152.679 496.319 163.979 501 176 501h128c12.021 0 23.321-4.681 31.819-13.18 8.5-8.499 13.181-19.8 13.181-31.82s-4.681-23.321-13.18-31.819C327.321 415.681 316.02 411 304 411h-19v-6h147.005c9.877-.009 19.164-3.861 26.148-10.847 6.985-6.984 10.838-16.271 10.847-26.153V162.309c28.021-22.179 38.745-58.362 27.32-92.91M303.945 437a18.88 18.88 0 0 1 13.301 5.639c3.542 3.58 5.492 8.325 5.492 13.361s-1.95 9.781-5.492 13.361S308.98 474.944 304 475H176.057a18.9 18.9 0 0 1-13.297-5.639A18.9 18.9 0 0 1 157.27 456a18.9 18.9 0 0 1 5.49-13.36 18.9 18.9 0 0 1 13.272-5.64h20.919a45.23 45.23 0 0 0 14.686 21.698A45.2 45.2 0 0 0 240 468.743a45.2 45.2 0 0 0 28.364-10.045A45.23 45.23 0 0 0 283.05 437zM259 423.944a18.9 18.9 0 0 1-5.64 13.297c-3.58 3.541-8.325 5.49-13.36 5.49s-9.78-1.949-13.36-5.49c-3.581-3.54-5.583-8.263-5.64-13.241v-19h38zM48.006 85h283.625c-3.258 22.679 2.962 45 17.813 63.438 16.523 20.515 41.558 32.763 66.965 32.763 9.076 0 17.99-1.522 26.59-4.533V347H37V96.006a10.95 10.95 0 0 1 3.228-7.778A10.95 10.95 0 0 1 48.006 85M357 96.1c1.727-40.375 31.78-58.769 58.999-58.769 27.225 0 57.284 18.391 59.001 58.77-.046 15.714-6.188 30.483-17.302 41.598-11.14 11.14-25.95 17.284-41.692 17.302-15.753-.018-30.563-6.163-41.703-17.303C363.188 126.584 357.046 111.814 357 96.1M441.796 373a11 11 0 0 1-2.023 2.771 10.95 10.95 0 0 1-7.772 3.229H48.007a10.95 10.95 0 0 1-7.779-3.228A11.1 11.1 0 0 1 38.204 373z M442.344 66.344 408 100.688l-10.344-10.344a8.003 8.003 0 0 0-11.203.109 8 8 0 0 0-.11 11.203l15.999 16a8 8 0 0 0 5.657 2.344 8 8 0 0 0 5.658-2.344l40-40a8.003 8.003 0 0 0-.121-11.192 8.003 8.003 0 0 0-11.192-.12"
                          ></path>
                        </svg>
                      </div>
                      <div>
                        <h3 className="font-semibold text-sm mb-0">CDS</h3>
                        <p className="text-gray-400 text-xs">Customer Display System</p>
                      </div>
                    </div>
                    <div
                      onClick={(e) => {
                        e.preventDefault();
                        navigate("/grabfood");
                        setIsFeaturesDropdownOpen(false);
                      }}
                      className="cursor-pointer hover:text-vern hover:bg-gray-900 p-3 rounded-lg transition-colors duration-200 flex items-center space-x-3"
                    >
                      <div className="w-6 h-6 flex items-center justify-center">
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M2 14.803v6.447c0 .414.336.75.75.75h1.614a.75.75 0 0 0 .74-.627L5.5 19h13l.395 2.373a.75.75 0 0 0 .74.627h1.615a.75.75 0 0 0 .75-.75v-6.447a5.954 5.954 0 0 0-1-3.303l-.78-1.17a1.994 1.994 0 0 1-.178-.33h.994a.75.75 0 0 0 .671-.415l.25-.5A.75.75 0 0 0 21.287 8H19.6l-.31-1.546a2.5 2.5 0 0 0-1.885-1.944C15.943 4.17 14.141 4 12 4c-2.142 0-3.943.17-5.405.51a2.5 2.5 0 0 0-1.886 1.944L4.399 8H2.714a.75.75 0 0 0-.67 1.085l.25.5a.75.75 0 0 0 .67.415h.995a1.999 1.999 0 0 1-.178.33L3 11.5c-.652.978-1 2.127-1 3.303zm15.961-4.799a4 4 0 0 0 .34.997H5.699c.157-.315.271-.65.34-.997l.632-3.157a.5.5 0 0 1 .377-.39C8.346 6.157 10 6 12 6c2 0 3.654.156 4.952.458a.5.5 0 0 1 .378.389l.631 3.157zM5.5 16a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3zM20 14.5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0z"
                          ></path>
                        </svg>
                      </div>
                      <div>
                        <h3 className="font-semibold text-sm mb-0">GrabFood</h3>
                        <p className="text-gray-400 text-xs">GrabFood POS Integration</p>
                      </div>
                    </div>
                    <div
                      onClick={(e) => {
                        e.preventDefault();
                        navigate("/qr-order");
                        setIsFeaturesDropdownOpen(false);
                      }}
                      className="cursor-pointer hover:text-vern hover:bg-gray-900 p-3 rounded-lg transition-colors duration-200 flex items-center space-x-3"
                    >
                      <div className="w-6 h-6 flex items-center justify-center">
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="1"
                            d="M21 7.505a1.5 1.5 0 0 1-1.5 1.5h-3a1.5 1.5 0 0 1-1.5-1.5V4.5A1.5 1.5 0 0 1 16.5 3h3A1.5 1.5 0 0 1 21 4.5v3.005zM9 7.5V4.504a1.5 1.5 0 0 0-1.498-1.5l-3-.003A1.5 1.5 0 0 0 3 4.5V7.5A1.5 1.5 0 0 0 4.5 9h3A1.5 1.5 0 0 0 9 7.5zM5 7V5.001l2 .002V7H5zm-.502 8.004 3-.003A1.5 1.5 0 0 1 9 16.5V19.5A1.5 1.5 0 0 1 7.5 21h-3A1.5 1.5 0 0 1 3 19.5v-2.996a1.5 1.5 0 0 1 1.498-1.5zM7 19H5v-1.997l2-.002V19zM19 5v2.005h-2V5h2zm0 11a1 1 0 1 1 2 0v3.5a1.5 1.5 0 0 1-1.498 1.5l-3.5.005a1 1 0 1 1-.003-2L19 19V16zM11 4v7H4a1 1 0 1 0 0 2h8a1 1 0 0 0 1-1V4a1 1 0 1 0-2 0zm2 16a1 1 0 1 1-2 0v-4a1 1 0 1 1 2 0v4zm3-3a1 1 0 0 0 1-1v-3h3a1 1 0 1 0 0-2h-4a1 1 0 0 0-1 1v4a1 1 0 0 0 1 1z"
                          ></path>
                        </svg>
                      </div>
                      <div>
                        <h3 className="font-semibold text-sm mb-0">QR Order</h3>
                        <p className="text-gray-400 text-xs">Ordering Made Easy</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <a
              onClick={(e) => {
                e.preventDefault();
                navigate("/pricing"); /* Pricing page UI */
              }}
              href="/pricing"
              className="text-white hover:text-vern"
            >
              PRICING
            </a>
            <a
              onClick={(e) => {
                e.preventDefault();
                navigate("/about-us");
              }}
              href="/about-us"
              className="text-white hover:text-vern"
            >
              ABOUT US
            </a>
            <a
              onClick={(e) => {
                e.preventDefault();
                navigate("/contact-us");
              }}
              href="/contact-us"
              className="text-white hover:text-vern"
            >
              CONTACT
            </a>
          </nav>
          {/* Mobile Menu Button */}
          <div className="md:hidden">
            <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="text-white focus:outline-none">
              <Menu size={24} />
            </button>
          </div>
          {/* Header Buttons */}
          <div className="hidden md:flex space-x-4">
            {/* Login Button */}
            <a
              href="https://pos.vernpos.my/login"
              target="_blank"
              rel="noopener noreferrer"
              className="border-2 border-white hover:border-red-400 text-white hover:text-red-400 px-6 py-2 rounded-full transition-colors duration-200"
            >
              LOGIN
            </a>

            {/* <button
              onClick={(e) => {
                e.preventDefault();
                navigate("/register");
              }}
              className="border-2 border-white hover:border-red-400 text-white hover:text-red-400 px-6 py-2 rounded-full transition-colors duration-200"
            >
              REGISTER
            </button> */}

            {/* <button
              onClick={(e) => {
                e.preventDefault();
                navigate("/register");
              }}
              className="border-2 border-white hover:border-red-400 text-white hover:text-red-400 px-6 py-2 rounded-full transition-colors duration-200"
            >
              PRICING
            </button> */}

            {/* Get a Quote Button */}
            <button
              onClick={(e) => {
                e.preventDefault();
                navigate("/contact-us");
              }}
              className="bg-vern hover:bg-red-400 text-white hover:text-white px-6 py-2 rounded-full transition-colors duration-200"
            >
              GET A QUOTE
            </button>
          </div>
          {/* Mobile Menu */}
          {isMenuOpen && (
            <div className="absolute top-20 mt-4 left-6 right-6 z-50 md:hidden" style={{ animation: "fadeIn 0.3s ease-out" }}>
              {/* Simple Menu Container */}
              <div className="bg-black rounded-xl shadow-lg overflow-hidden">
                <div className="px-6 py-6 flex flex-col items-center w-full">
                  <nav className="flex flex-col w-full items-center">
                    {/* Navigation Links */}
                    <div className="space-y-4 w-full mb-5">
                      {" "}
                      <button
                        onClick={() => {
                          navigate("/");
                          setIsMenuOpen(false);
                        }}
                        className="flex items-center space-x-3 text-white hover:text-red-400 w-full py-2 px-3 rounded-lg hover:bg-gray-800 transition-colors duration-200"
                      >
                        <Home size={18} className="text-white" />
                        <span className="font-medium">HOME</span>
                      </button>
                      {/* Products Dropdown in Mobile */}
                      <div className="w-full">
                        <button
                          onClick={() => setIsMobileProductsOpen(!isMobileProductsOpen)}
                          className={`flex items-center space-x-3 text-white ${
                            isMobileProductsOpen ? "text-red-400" : ""
                          } w-full py-2 px-3 rounded-lg hover:bg-gray-800 transition-colors duration-200`}
                        >
                          <div className="flex items-center space-x-3">
                            <Box size={18} className="text-white" />
                            <span className="font-medium">PRODUCTS</span>
                          </div>
                          <svg
                            className={`w-4 h-4 transition-transform duration-200 ${isMobileProductsOpen ? "rotate-180" : ""}`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                          </svg>
                        </button>
                        {/* Mobile Products Submenu */}
                        {isMobileProductsOpen && (
                          <div className="ml-6 mt-2 space-y-3">
                            {/* Mobile submenu items for Software */}
                            <div className="md:hidden space-y-1">
                              <div
                                onClick={() => {
                                  navigate("/software");
                                  setIsMenuOpen(false);
                                  setIsMobileProductsOpen(false);
                                }}
                                className="flex items-center space-x-3 text-white hover:text-red-400 w-full py-2 px-3 rounded-lg hover:bg-gray-800 transition-colors duration-200"
                              >
                                <div className="w-6 h-6 flex items-center justify-center">
                                  <svg className="w-5 h-5 text-white" fill="#FFF" stroke="currentColor" viewBox="0 0 512 512">
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth="2"
                                      d="M435.2 85.333h-85.333V68.266c0-9.386-7.68-17.066-17.067-17.066H179.2c-9.386 0-17.066 7.68-17.066 17.066v17.067H76.8c-14.08 0-25.6 11.52-25.6 25.601v238.933c0 14.08 11.52 25.6 25.6 25.6h128v34.987c-19.456 3.925-34.133 21.162-34.133 41.813 0 4.693 3.84 8.533 8.533 8.533h153.6c4.694 0 8.534-3.84 8.534-8.533 0-20.564-14.678-37.803-34.134-41.813v-34.987h128c14.08 0 25.6-11.52 25.6-25.6V110.933c0-14.08-11.52-25.6-25.6-25.6zm-256-17.067h153.6v187.733H179.2zM76.8 102.399h85.333v153.6c0 9.387 7.68 17.067 17.066 17.067h153.6c9.388 0 17.067-7.681 17.067-17.067v-153.6H435.2c4.693 0 8.534 3.84 8.534 8.534v204.8H68.267v-204.8c0-4.694 3.84-8.534 8.533-8.534zm246.016 341.334H189.184c3.499-9.899 12.971-17.067 24.15-17.067h85.333c11.093 0 20.651 7.168 24.149 17.067zm-32.682-34.134h-68.267v-34.133h68.267zm145.066-51.2H76.8c-4.693 0-8.533-3.841-8.533-8.533v-17.067h375.467v17.067c0 4.693-3.84 8.533-8.534 8.533z"
                                    />
                                    <path d="M204.8 136.534h102.4c4.692 0 8.532-3.84 8.532-8.533V93.867c0-4.693-3.84-8.533-8.532-8.533H204.8c-4.693 0-8.533 3.84-8.533 8.533v34.134c0 4.693 3.84 8.533 8.533 8.533m8.533-34.133h85.334v17.066h-85.334zm8.533 51.199h-8.533c-4.693 0-8.533 3.84-8.533 8.533s3.84 8.533 8.533 8.533h8.533c4.694 0 8.534-3.84 8.534-8.533s-3.84-8.533-8.534-8.533m38.402 0h-8.534c-4.694 0-8.534 3.84-8.534 8.533s3.84 8.533 8.534 8.533h8.534c4.692 0 8.532-3.84 8.532-8.533s-3.84-8.533-8.532-8.533m38.398 0h-8.533c-4.693 0-8.533 3.84-8.533 8.533s3.84 8.533 8.533 8.533h8.533c4.693 0 8.534-3.84 8.534-8.533s-3.841-8.533-8.534-8.533m-76.8 34.132h-8.533c-4.693 0-8.533 3.84-8.533 8.534 0 4.693 3.84 8.533 8.533 8.533h8.533c4.694 0 8.534-3.84 8.534-8.533 0-4.694-3.84-8.534-8.534-8.534m38.402 0h-8.534c-4.694 0-8.534 3.84-8.534 8.534 0 4.693 3.84 8.533 8.534 8.533h8.534c4.692 0 8.532-3.84 8.532-8.533 0-4.694-3.84-8.534-8.532-8.534m38.398 0h-8.533c-4.693 0-8.533 3.84-8.533 8.534 0 4.693 3.84 8.533 8.533 8.533h8.533c4.693 0 8.534-3.84 8.534-8.533 0-4.694-3.841-8.534-8.534-8.534m-76.8 34.135h-8.533c-4.693 0-8.533 3.84-8.533 8.534 0 4.693 3.84 8.533 8.533 8.533h8.533c4.694 0 8.534-3.84 8.534-8.533 0-4.694-3.84-8.534-8.534-8.534m38.402 0h-8.534c-4.694 0-8.534 3.84-8.534 8.534 0 4.693 3.84 8.533 8.534 8.533h8.534c4.692 0 8.532-3.84 8.532-8.533 0-4.694-3.84-8.534-8.532-8.534m38.398 0h-8.533c-4.693 0-8.533 3.84-8.533 8.534 0 4.693 3.84 8.533 8.533 8.533h8.533c4.693 0 8.534-3.84 8.534-8.533 0-4.694-3.841-8.534-8.534-8.534"></path>
                                  </svg>
                                </div>
                                <div className="flex flex-col items-start">
                                  <span className="font-medium">POS Software</span>
                                  <span className="text-sm text-gray-400">POS System Solutions</span>
                                </div>
                                {/* <div className="flex items-center focus:outline-none">
                                  <svg
                                    className={`ml-1 w-4 h-4 transition-transform duration-200 ${isSoftwareDropdownOpen ? "rotate-180" : ""}`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                                  </svg>
                                </div> */}
                                {/* Software Submenu Dropdown */}
                                {/* <div
                                  className={`dropdown-container absolute top-full left-0 md:top-0 md:left-full md:ml-2 w-72 bg-black rounded-xl shadow-xl border border-gray-800 transition-all duration-300 z-50 ${
                                    isSoftwareDropdownOpen
                                      ? "opacity-100 visible transform translate-y-0 md:translate-x-0"
                                      : "opacity-0 invisible transform -translate-y-2 md:-translate-x-2"
                                  }`}
                                  onMouseEnter={() => setIsSoftwareDropdownOpen(true)}
                                  onMouseLeave={() => setIsSoftwareDropdownOpen(false)}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <div className="p-4">
                                    <div className="space-y-2">
                                      <div
                                        onClick={(e) => {
                                          e.preventDefault();
                                          navigate("/software");
                                          setIsProductsDropdownOpen(false);
                                        }}
                                        className="cursor-pointer hover:bg-gray-900 p-3 rounded-lg transition-colors duration-200 flex items-center space-x-3"
                                      >
                                        <div className="w-6 h-6 flex items-center justify-center">
                                          <svg className="w-5 h-5 text-white" fill="#FFF" stroke="currentColor" viewBox="0 0 512 512">
                                            <path
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                              strokeWidth="2"
                                              d="M435.2 85.333h-85.333V68.266c0-9.386-7.68-17.066-17.067-17.066H179.2c-9.386 0-17.066 7.68-17.066 17.066v17.067H76.8c-14.08 0-25.6 11.52-25.6 25.601v238.933c0 14.08 11.52 25.6 25.6 25.6h128v34.987c-19.456 3.925-34.133 21.162-34.133 41.813 0 4.693 3.84 8.533 8.533 8.533h153.6c4.694 0 8.534-3.84 8.534-8.533 0-20.564-14.678-37.803-34.134-41.813v-34.987h128c14.08 0 25.6-11.52 25.6-25.6V110.933c0-14.08-11.52-25.6-25.6-25.6zm-256-17.067h153.6v187.733H179.2zM76.8 102.399h85.333v153.6c0 9.387 7.68 17.067 17.066 17.067h153.6c9.388 0 17.067-7.681 17.067-17.067v-153.6H435.2c4.693 0 8.534 3.84 8.534 8.534v204.8H68.267v-204.8c0-4.694 3.84-8.534 8.533-8.534zm246.016 341.334H189.184c3.499-9.899 12.971-17.067 24.15-17.067h85.333c11.093 0 20.651 7.168 24.149 17.067zm-32.682-34.134h-68.267v-34.133h68.267zm145.066-51.2H76.8c-4.693 0-8.533-3.841-8.533-8.533v-17.067h375.467v17.067c0 4.693-3.84 8.533-8.534 8.533z"
                                            />
                                            <path d="M204.8 136.534h102.4c4.692 0 8.532-3.84 8.532-8.533V93.867c0-4.693-3.84-8.533-8.532-8.533H204.8c-4.693 0-8.533 3.84-8.533 8.533v34.134c0 4.693 3.84 8.533 8.533 8.533m8.533-34.133h85.334v17.066h-85.334zm8.533 51.199h-8.533c-4.693 0-8.533 3.84-8.533 8.533s3.84 8.533 8.533 8.533h8.533c4.694 0 8.534-3.84 8.534-8.533s-3.84-8.533-8.534-8.533m38.402 0h-8.534c-4.694 0-8.534 3.84-8.534 8.533s3.84 8.533 8.534 8.533h8.534c4.692 0 8.532-3.84 8.532-8.533s-3.84-8.533-8.532-8.533m38.398 0h-8.533c-4.693 0-8.533 3.84-8.533 8.533s3.84 8.533 8.533 8.533h8.533c4.693 0 8.534-3.84 8.534-8.533s-3.841-8.533-8.534-8.533m-76.8 34.132h-8.533c-4.693 0-8.533 3.84-8.533 8.534 0 4.693 3.84 8.533 8.533 8.533h8.533c4.694 0 8.534-3.84 8.534-8.533 0-4.694-3.84-8.534-8.534-8.534m38.402 0h-8.534c-4.694 0-8.534 3.84-8.534 8.534 0 4.693 3.84 8.533 8.534 8.533h8.534c4.692 0 8.532-3.84 8.532-8.533 0-4.694-3.84-8.534-8.532-8.534m38.398 0h-8.533c-4.693 0-8.533 3.84-8.533 8.534 0 4.693 3.84 8.533 8.533 8.533h8.533c4.693 0 8.534-3.84 8.534-8.533 0-4.694-3.841-8.534-8.534-8.534m-76.8 34.135h-8.533c-4.693 0-8.533 3.84-8.533 8.534 0 4.693 3.84 8.533 8.533 8.533h8.533c4.694 0 8.534-3.84 8.534-8.533 0-4.694-3.84-8.534-8.534-8.534m38.402 0h-8.534c-4.694 0-8.534 3.84-8.534 8.534 0 4.693 3.84 8.533 8.534 8.533h8.534c4.692 0 8.532-3.84 8.532-8.533 0-4.694-3.84-8.534-8.532-8.534m38.398 0h-8.533c-4.693 0-8.533 3.84-8.533 8.534 0 4.693 3.84 8.533 8.533 8.533h8.533c4.693 0 8.534-3.84 8.534-8.533 0-4.694-3.841-8.534-8.534-8.534"></path>
                                          </svg>
                                        </div>
                                        <div>
                                          <h3 className="text-white font-semibold text-sm mb-0">POS</h3>
                                          <p className="text-gray-400 text-xs">POS System Solutions</p>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div> */}
                              </div>
                            </div>
                            <button
                              onClick={() => {
                                navigate("/hardware");
                                setIsMenuOpen(false);
                                setIsMobileProductsOpen(false);
                              }}
                              className="flex items-center space-x-3 text-white hover:text-red-400 w-full py-2 px-3 rounded-lg hover:bg-gray-800 transition-colors duration-200"
                            >
                              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth="2"
                                  d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                                ></path>
                              </svg>
                              <div className="flex flex-col items-start">
                                <span className="font-medium">Hardware</span>
                                <span className="text-sm text-gray-400">AIO Solution For Your Business</span>
                              </div>
                            </button>
                          </div>
                        )}
                      </div>
                      <div className="w-full">
                        <button
                          onClick={() => setIsMobileFeaturesOpen(!isMobileFeaturesOpen)}
                          className={`flex items-center space-x-3 text-white ${
                            isMobileFeaturesOpen ? "text-red-400" : ""
                          } w-full py-2 px-3 rounded-lg hover:bg-gray-800 transition-colors duration-200`}
                        >
                          <div className="flex items-center space-x-3">
                            <Box size={18} className="text-white" />
                            <span className="font-medium">FEATURES</span>
                          </div>
                          <svg
                            className={`w-4 h-4 transition-transform duration-200 ${isMobileFeaturesOpen ? "rotate-180" : ""}`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                          </svg>
                        </button>
                        {/* Mobile Features Submenu */}
                        {isMobileFeaturesOpen && (
                          <div className="ml-6 mt-2 space-y-3">
                            <div
                              onClick={(e) => {
                                e.preventDefault();
                                navigate("/kds");
                                setIsProductsDropdownOpen(false);
                              }}
                              className="cursor-pointer hover:bg-gray-900 p-3 rounded-lg transition-colors duration-200 flex items-center space-x-3"
                            >
                              <div className="w-6 h-6 flex items-center justify-center">
                                <svg className="w-5 h-5 text-white" fill="#FFF" stroke="currentColor" viewBox="0 0 512 512">
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M456 58H56a24.026 24.026 0 0 0-24 24v272a24.026 24.026 0 0 0 24 24h146.14l-7.577 36H192a32.03 32.03 0 0 0-22.617 9.383A32.03 32.03 0 0 0 160 446a8 8 0 0 0 2.343 5.657A8 8 0 0 0 168 454h176a8 8 0 0 0 5.657-2.343A8 8 0 0 0 352 446a32.03 32.03 0 0 0-9.383-22.617A32.03 32.03 0 0 0 320 414h-2.563l-7.577-36H456a24.03 24.03 0 0 0 24-24V82a24.03 24.03 0 0 0-24-24M333.852 438H178.148A16 16 0 0 1 192 430h128a16 16 0 0 1 13.852 8m-32.766-24h-90.172l7.578-36h75.016zM464 354a8 8 0 0 1-2.345 5.655A8 8 0 0 1 456 362H56a8.01 8.01 0 0 1-8-8v-24h416zm0-40H48V82a8.01 8.01 0 0 1 8-8h400a8.01 8.01 0 0 1 8 8z M383.499 231.852h-6.714A121.49 121.49 0 0 0 264 131.364v-13.641a7.997 7.997 0 0 0-8-8 8 8 0 0 0-8 8v13.641a121.49 121.49 0 0 0-112.782 100.488h-6.719a8.36 8.36 0 0 0-5.695 2.726 8.82 8.82 0 0 0-2.297 6.071 32.93 32.93 0 0 0 31.297 33.629h207.961a31.2 31.2 0 0 0 27.07-16.004 36.93 36.93 0 0 0 4.664-18.422 7.98 7.98 0 0 0-2.342-5.658 8 8 0 0 0-5.658-2.342M256 146.988a105.92 105.92 0 0 1 104.694 84.864H151.312A105.91 105.91 0 0 1 256 146.988M372.906 250.41a15 15 0 0 1-13.141 7.867H151.804a15.3 15.3 0 0 1-13.742-10.425h4.109l227.664.039c.065 0 .122-.038.186-.039h4.088q-.51 1.322-1.203 2.558"
                                  ></path>
                                </svg>
                              </div>
                              <div>
                                <h3 className="text-white font-semibold text-sm mb-0">KDS</h3>
                                <p className="text-gray-400 text-xs">Kitchen Display System</p>
                              </div>
                            </div>
                            <div
                              onClick={(e) => {
                                e.preventDefault();
                                navigate("/cds");
                                setIsProductsDropdownOpen(false);
                              }}
                              className="cursor-pointer hover:bg-gray-900 p-3 rounded-lg transition-colors duration-200 flex items-center space-x-3"
                            >
                              <div className="w-6 h-6 flex items-center justify-center">
                                <svg className="w-5 h-5 text-white" fill="#FFF" stroke="currentColor" viewBox="0 0 512 512">
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M337.446 142.553C330.65 135.757 321.614 132.009 312 132H167.996c-9.61.009-18.646 3.757-25.443 10.553C135.757 149.35 132.009 158.386 132 168v136.004c.009 9.61 3.757 18.646 10.553 25.442C149.35 336.243 158.386 339.991 168 340h144.004c9.61-.009 18.646-3.757 25.442-10.554C344.243 322.65 347.991 313.614 348 304V167.996c-.009-9.61-3.757-18.646-10.554-25.443m-141.238 53.239c5.286 5.285 12.313 8.201 19.792 8.208h48.004c7.475-.008 14.502-2.923 19.787-8.208 5.286-5.286 8.201-12.313 8.209-19.792v-20h19.994a11.95 11.95 0 0 1 8.485 3.52A11.95 11.95 0 0 1 324 168v135.994a11.95 11.95 0 0 1-3.52 8.484 11.94 11.94 0 0 1-8.48 3.521H168.005a11.95 11.95 0 0 1-8.484-3.521 11.94 11.94 0 0 1-3.521-8.479V168.005a11.94 11.94 0 0 1 3.52-8.484 11.95 11.95 0 0 1 8.485-3.521H188v20.004c.008 7.475 2.923 14.502 8.208 19.788M212 176v-20h56v19.998c-.001 1.067-.417 2.072-1.174 2.828S265.065 180 264 180h-47.998c-1.067 0-2.072-.417-2.828-1.174S212 177.065 212 176 M167.568 255.574c-2.229 2.261-3.456 5.253-3.456 8.426s1.228 6.165 3.456 8.426 5.204 3.529 8.432 3.574h48.056a11.93 11.93 0 0 0 8.376-3.574c2.228-2.26 3.455-5.252 3.455-8.426s-1.227-6.166-3.455-8.426-5.203-3.529-8.432-3.574h-48.057a11.93 11.93 0 0 0-8.375 3.574M208 284h-32.056c-3.173.045-6.148 1.313-8.376 3.574s-3.456 5.253-3.456 8.426 1.228 6.165 3.456 8.426c2.227 2.259 5.201 3.528 8.432 3.574h32.058a11.94 11.94 0 0 0 8.375-3.574c2.228-2.26 3.455-5.252 3.455-8.426s-1.227-6.166-3.455-8.426c-2.229-2.261-5.204-3.529-8.433-3.574 M496.32 69.399C484.616 34.006 453.093 11.083 415.993 11a84.9 84.9 0 0 0-46.513 13.912A84.9 84.9 0 0 0 339.526 59H47.995c-9.877.01-19.164 3.862-26.149 10.847S11.01 86.118 11 96v272.005c.01 9.878 3.862 19.164 10.847 26.149S38.119 404.991 48 405h147v6h-19c-12.021 0-23.321 4.681-31.819 13.18-8.5 8.499-13.181 19.8-13.181 31.82 0 12.021 4.681 23.321 13.18 31.819C152.679 496.319 163.979 501 176 501h128c12.021 0 23.321-4.681 31.819-13.18 8.5-8.499 13.181-19.8 13.181-31.82s-4.681-23.321-13.18-31.819C327.321 415.681 316.02 411 304 411h-19v-6h147.005c9.877-.009 19.164-3.861 26.148-10.847 6.985-6.984 10.838-16.271 10.847-26.153V162.309c28.021-22.179 38.745-58.362 27.32-92.91M303.945 437a18.88 18.88 0 0 1 13.301 5.639c3.542 3.58 5.492 8.325 5.492 13.361s-1.95 9.781-5.492 13.361S308.98 474.944 304 475H176.057a18.9 18.9 0 0 1-13.297-5.639A18.9 18.9 0 0 1 157.27 456a18.9 18.9 0 0 1 5.49-13.36 18.9 18.9 0 0 1 13.272-5.64h20.919a45.23 45.23 0 0 0 14.686 21.698A45.2 45.2 0 0 0 240 468.743a45.2 45.2 0 0 0 28.364-10.045A45.23 45.23 0 0 0 283.05 437zM259 423.944a18.9 18.9 0 0 1-5.64 13.297c-3.58 3.541-8.325 5.49-13.36 5.49s-9.78-1.949-13.36-5.49c-3.581-3.54-5.583-8.263-5.64-13.241v-19h38zM48.006 85h283.625c-3.258 22.679 2.962 45 17.813 63.438 16.523 20.515 41.558 32.763 66.965 32.763 9.076 0 17.99-1.522 26.59-4.533V347H37V96.006a10.95 10.95 0 0 1 3.228-7.778A10.95 10.95 0 0 1 48.006 85M357 96.1c1.727-40.375 31.78-58.769 58.999-58.769 27.225 0 57.284 18.391 59.001 58.77-.046 15.714-6.188 30.483-17.302 41.598-11.14 11.14-25.95 17.284-41.692 17.302-15.753-.018-30.563-6.163-41.703-17.303C363.188 126.584 357.046 111.814 357 96.1M441.796 373a11 11 0 0 1-2.023 2.771 10.95 10.95 0 0 1-7.772 3.229H48.007a10.95 10.95 0 0 1-7.779-3.228A11.1 11.1 0 0 1 38.204 373z M442.344 66.344 408 100.688l-10.344-10.344a8.003 8.003 0 0 0-11.203.109 8 8 0 0 0-.11 11.203l15.999 16a8 8 0 0 0 5.657 2.344 8 8 0 0 0 5.658-2.344l40-40a8.003 8.003 0 0 0-.121-11.192 8.003 8.003 0 0 0-11.192-.12"
                                  ></path>
                                </svg>
                              </div>
                              <div>
                                <h3 className="text-white font-semibold text-sm mb-0">CDS</h3>
                                <p className="text-gray-400 text-xs">Customer Display System</p>
                              </div>
                            </div>
                            <button
                              onClick={() => {
                                navigate("/grabfood");
                                setIsMenuOpen(false);
                                setIsMobileFeaturesOpen(false);
                              }}
                              className="flex items-center space-x-3 text-white hover:text-red-400 w-full py-2 px-3 rounded-lg hover:bg-gray-800 transition-colors duration-200"
                            >
                              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth="2"
                                  d="M2 14.803v6.447c0 .414.336.75.75.75h1.614a.75.75 0 0 0 .74-.627L5.5 19h13l.395 2.373a.75.75 0 0 0 .74.627h1.615a.75.75 0 0 0 .75-.75v-6.447a5.954 5.954 0 0 0-1-3.303l-.78-1.17a1.994 1.994 0 0 1-.178-.33h.994a.75.75 0 0 0 .671-.415l.25-.5A.75.75 0 0 0 21.287 8H19.6l-.31-1.546a2.5 2.5 0 0 0-1.885-1.944C15.943 4.17 14.141 4 12 4c-2.142 0-3.943.17-5.405.51a2.5 2.5 0 0 0-1.886 1.944L4.399 8H2.714a.75.75 0 0 0-.67 1.085l.25.5a.75.75 0 0 0 .67.415h.995a1.999 1.999 0 0 1-.178.33L3 11.5c-.652.978-1 2.127-1 3.303zm15.961-4.799a4 4 0 0 0 .34.997H5.699c.157-.315.271-.65.34-.997l.632-3.157a.5.5 0 0 1 .377-.39C8.346 6.157 10 6 12 6c2 0 3.654.156 4.952.458a.5.5 0 0 1 .378.389l.631 3.157zM5.5 16a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3zM20 14.5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0z"
                                ></path>
                              </svg>
                              <div className="flex flex-col items-start">
                                <span className="font-medium">GrabFood</span>
                                <span className="text-sm text-gray-400">GrabFood POS Integration</span>
                              </div>
                            </button>
                            <button
                              onClick={() => {
                                navigate("/qr-order");
                                setIsMenuOpen(false);
                                setIsMobileFeaturesOpen(false);
                              }}
                              className="flex items-center space-x-3 text-white hover:text-red-400 w-full py-2 px-3 rounded-lg hover:bg-gray-800 transition-colors duration-200"
                            >
                              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth="1"
                                  d="M21 7.505a1.5 1.5 0 0 1-1.5 1.5h-3a1.5 1.5 0 0 1-1.5-1.5V4.5A1.5 1.5 0 0 1 16.5 3h3A1.5 1.5 0 0 1 21 4.5v3.005zM9 7.5V4.504a1.5 1.5 0 0 0-1.498-1.5l-3-.003A1.5 1.5 0 0 0 3 4.5V7.5A1.5 1.5 0 0 0 4.5 9h3A1.5 1.5 0 0 0 9 7.5zM5 7V5.001l2 .002V7H5zm-.502 8.004 3-.003A1.5 1.5 0 0 1 9 16.5V19.5A1.5 1.5 0 0 1 7.5 21h-3A1.5 1.5 0 0 1 3 19.5v-2.996a1.5 1.5 0 0 1 1.498-1.5zM7 19H5v-1.997l2-.002V19zM19 5v2.005h-2V5h2zm0 11a1 1 0 1 1 2 0v3.5a1.5 1.5 0 0 1-1.498 1.5l-3.5.005a1 1 0 1 1-.003-2L19 19V16zM11 4v7H4a1 1 0 1 0 0 2h8a1 1 0 0 0 1-1V4a1 1 0 1 0-2 0zm2 16a1 1 0 1 1-2 0v-4a1 1 0 1 1 2 0v4zm3-3a1 1 0 0 0 1-1v-3h3a1 1 0 1 0 0-2h-4a1 1 0 0 0-1 1v4a1 1 0 0 0 1 1z"
                                ></path>
                              </svg>
                              <div className="flex flex-col items-start">
                                <span className="font-medium">QR Order</span>
                                <span className="text-sm text-gray-400">Ordering Made Easy</span>
                              </div>
                            </button>
                          </div>
                        )}
                      </div>
                      <button
                        onClick={() => {
                          navigate("/pricing");
                          setIsMenuOpen(false);
                        }}
                        className="flex items-center space-x-3 text-white hover:text-red-400 w-full py-2 px-3 rounded-lg hover:bg-gray-800 transition-colors duration-200"
                      >
                        <Mail size={18} className="text-white" />
                        <span className="font-medium">PRICING</span>
                      </button>
                      <button
                        onClick={() => {
                          navigate("/about-us");
                          setIsMenuOpen(false);
                        }}
                        className="flex items-center space-x-3 text-white hover:text-red-400 w-full py-2 px-3 rounded-lg hover:bg-gray-800 transition-colors duration-200"
                      >
                        <Info size={18} className="text-white" />
                        <span className="font-medium">ABOUT US</span>
                      </button>
                      <button
                        onClick={() => {
                          navigate("/contact-us");
                          setIsMenuOpen(false);
                        }}
                        className="flex items-center space-x-3 text-white hover:text-red-400 w-full py-2 px-3 rounded-lg hover:bg-gray-800 transition-colors duration-200"
                      >
                        <Mail size={18} className="text-white" />
                        <span className="font-medium">CONTACT</span>
                      </button>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex flex-col space-y-3 w-full mt-2">
                      <a
                        href="https://pos.vernpos.my/login"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="border-2 border-white text-white hover:text-red-400 hover:border-red-400 px-6 py-3 rounded-full flex items-center justify-center transition-colors duration-200 font-medium w-full"
                        onClick={() => setIsMenuOpen(false)}
                      >
                        LOGIN
                      </a>
                      {/* <button
                        onClick={() => {
                          navigate("/register");
                          setIsMenuOpen(false);
                        }}
                        className="border-2 border-white text-white hover:text-red-400 hover:border-red-400 px-6 py-3 rounded-full flex items-center justify-center transition-colors duration-200 font-medium w-full"
                      >
                        REGISTER
                      </button>
                      <button
                        onClick={() => {
                          navigate("/register");
                          setIsMenuOpen(false);
                        }}
                        className="border-2 border-white text-white hover:text-red-400 hover:border-red-400 px-6 py-3 rounded-full flex items-center justify-center transition-colors duration-200 font-medium w-full"
                      >
                        PRICING
                      </button> */}
                      <button
                        onClick={() => {
                          navigate("/contact-us");
                          setIsMenuOpen(false);
                        }}
                        className="bg-vern hover:bg-red-500 text-white px-6 py-3 rounded-full flex items-center justify-center transition-colors duration-200 font-medium w-full"
                      >
                        GET A QUOTE
                      </button>
                    </div>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </header>
      </div>
    </>
  );
}

export default Header;
